import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Autocomplete,
  Typography,
  Chip,
  Divider,
  Checkbox,
  Button,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { MaterialReactTable } from "material-react-table";
import { SingleAuditTablePlaceholder } from "../CompareTab/Components/TablePlaceholder";
import { useValidationContext } from "../validation.context";

// ─── Constants ────────────────────────────────────────────────

const auditMetricOptions = [
  "Quantity",
  "Dimension",
  "Cost",
  "Schedule",
  "Weight",
];

const comparisonPalette = [
  { bg: "#e3f2fd", border: "#1976d2", label: "Blue" },
  { bg: "#fce4ec", border: "#c2185b", label: "Pink" },
  { bg: "#e8f5e9", border: "#388e3c", label: "Green" },
  { bg: "#fff8e1", border: "#f9a825", label: "Amber" },
  { bg: "#f3e5f5", border: "#7b1fa2", label: "Purple" },
];

const defaultSelection = {
  lifecycle: "",
  referenceType: "",
  reference: "",
  revision: "",
};

const makeComparison = () => ({
  id: crypto.randomUUID(),
  lifecycle: "",
  referenceType: "",
  reference: "",
  revision: "",
});

// ─── Metric extraction — single source of truth for "what a metric means" ──
const metricAccessors = {
  Quantity: (m) => m.quantity,
  Dimension: (m) => m.dimension || m.length,
  Weight: (m) => m.weight,
  Cost: (m) =>
    m.mrqMaterials?.[0]?.cost ?? m.mrqMaterials?.[0]?.poMaterials?.[0]?.cost,
  Schedule: (m) => m.mrqMaterials?.[0]?.poMaterials?.[0]?.time,
};

const readMetric = (material, metric) => {
  const raw = metricAccessors[metric]?.(material);
  return raw === null || raw === undefined || raw === "" ? undefined : raw;
};

// ─── Derive dropdown options + filtered sheets from real drawings data ──────
function useDrawingOptions(drawings) {
  const lifecycleOptions = useMemo(
    () => [...new Set(drawings.map((d) => d.lifecycle).filter(Boolean))],
    [drawings],
  );

  const getReferenceTypeOptions = useCallback(
    (lifecycle) => [
      ...new Set(
        drawings
          .filter((d) => d.lifecycle === lifecycle)
          .map((d) => d.reference)
          .filter(Boolean),
      ),
    ],
    [drawings],
  );

  const getReferenceOptions = useCallback(
    (lifecycle, referenceType) => [
      ...new Set(
        drawings
          .filter(
            (d) => d.lifecycle === lifecycle && d.reference === referenceType,
          )
          .map((d) => d.dwgNumber)
          .filter(Boolean),
      ),
    ],
    [drawings],
  );

  const getRevisionOptions = useCallback(
    (lifecycle, referenceType, reference) => [
      ...new Set(
        drawings
          .filter(
            (d) =>
              d.lifecycle === lifecycle &&
              d.reference === referenceType &&
              d.dwgNumber === reference,
          )
          .map((d) => d.revision)
          .filter((r) => r !== null && r !== undefined),
      ),
    ],
    [drawings],
  );

  // A single (lifecycle, referenceType, reference, revision) can still match
  // multiple sheets (sheetNumber varies) — return them all, caller flattens materials.
  const getSheets = useCallback(
    (lifecycle, referenceType, reference, revision) =>
      drawings.filter(
        (d) =>
          d.lifecycle === lifecycle &&
          d.reference === referenceType &&
          d.dwgNumber === reference &&
          d.revision === revision,
      ),
    [drawings],
  );

  return {
    lifecycleOptions,
    getReferenceTypeOptions,
    getReferenceOptions,
    getRevisionOptions,
    getSheets,
  };
}

// ─── Merge primary + comparison materials into one row-per-component table ──
function computeStatus(valuesBySource, sourceKeys, metrics) {
  const present = sourceKeys.filter((k) => valuesBySource[k]);
  if (present.length < sourceKeys.length) return "Pending";

  let hasVariance = false;
  let majorVariance = false;

  metrics.forEach((metric) => {
    const raw = present
      .map((k) => valuesBySource[k]?.[metric])
      .filter((v) => v !== undefined);
    if (raw.length < 2) return;

    const nums = raw.map(Number);
    const allNumeric = nums.every((n) => !Number.isNaN(n));

    if (allNumeric) {
      const max = Math.max(...nums);
      const min = Math.min(...nums);
      if (max !== min) {
        hasVariance = true;
        const pctDiff = min === 0 ? Infinity : (max - min) / min;
        if (pctDiff > 0.2) majorVariance = true;
      }
    } else if (new Set(raw).size > 1) {
      hasVariance = true;
    }
  });

  if (majorVariance) return "Major Variance";
  if (hasVariance) return "Variance Found";
  return "Matched";
}

function buildMergedRows(primaryMaterials, comparisonMaterialsById, metrics) {
  const sourceKeys = ["primary", ...Object.keys(comparisonMaterialsById)];
  const rows = new Map();

  const upsert = (material, sourceKey) => {
    const uid = material.UID || material.tagNumber || material.id;
    if (!uid) return;

    if (!rows.has(uid)) {
      rows.set(uid, {
        semanticId: uid,
        component: material.description ?? "—",
        _valuesBySource: {},
      });
    }
    const row = rows.get(uid);
    row._valuesBySource[sourceKey] = {};
    metrics.forEach((metric) => {
      const value = readMetric(material, metric);
      row[`${sourceKey}__${metric}`] = value;
      row._valuesBySource[sourceKey][metric] = value;
    });
  };

  primaryMaterials.forEach((m) => upsert(m, "primary"));
  Object.entries(comparisonMaterialsById).forEach(([id, materials]) =>
    materials.forEach((m) => upsert(m, id)),
  );

  return [...rows.values()].map((row) => {
    const { _valuesBySource, ...rest } = row;
    return {
      ...rest,
      status: computeStatus(_valuesBySource, sourceKeys, metrics),
    };
  });
}

// ─── Component ────────────────────────────────────────────────
export default function SearchTab() {
  const theme = useTheme();
  const [selection, setSelection] = useState(defaultSelection);
  const [auditMetrics, setAuditMetrics] = useState([]);
  const [comparisonList, setComparisonList] = useState([]);
  const { plant } = useValidationContext();
  const drawings = plant?.drawings ?? [];

  const [searchInput, setSearchInput] = useState("");
  const [searchDrawing, setSearchDrawing] = useState(null);

  const searchOptions = useMemo(() => {
    if (!searchInput) return [];
    const q = searchInput.toLowerCase();
    return drawings.filter(
      (d) =>
        d.dwgNumber?.toLowerCase().includes(q) ||
        d.title?.toLowerCase().includes(q),
    );
  }, [drawings, searchInput]);

  const {
    lifecycleOptions,
    getReferenceTypeOptions,
    getReferenceOptions,
    getRevisionOptions,
    getSheets,
  } = useDrawingOptions(drawings);

  // ── Derived state ──────────────────────────────────────────
  const ready =
    !!searchDrawing ||
    (selection.lifecycle &&
      selection.referenceType &&
      selection.reference &&
      selection.revision);

  const canShowTable = ready && auditMetrics.length > 0;

  const primaryMaterials = useMemo(() => {
    if (searchDrawing) {
      return drawings
        .filter((d) => d.dwgNumber === searchDrawing.dwgNumber)
        .flatMap((s) => s.materials ?? []);
    }
    if (
      !selection.lifecycle ||
      !selection.referenceType ||
      !selection.reference ||
      !selection.revision
    ) {
      return [];
    }
    return getSheets(
      selection.lifecycle,
      selection.referenceType,
      selection.reference,
      selection.revision,
    ).flatMap((s) => s.materials ?? []);
  }, [selection, getSheets, searchDrawing]);

  const completedComparisons = useMemo(
    () =>
      comparisonList.filter(
        (c) => c.lifecycle && c.referenceType && c.reference && c.revision,
      ),
    [comparisonList],
  );

  const comparisonMaterialsById = useMemo(() => {
    const map = {};
    completedComparisons.forEach((c) => {
      map[c.id] = getSheets(
        c.lifecycle,
        c.referenceType,
        c.reference,
        c.revision,
      ).flatMap((s) => s.materials ?? []);
    });
    return map;
  }, [completedComparisons, getSheets]);

  const tableData = useMemo(
    () =>
      buildMergedRows(primaryMaterials, comparisonMaterialsById, auditMetrics),
    [primaryMaterials, comparisonMaterialsById, auditMetrics],
  );

  // ── Comparison CRUD ────────────────────────────────────────
  const addComparison = useCallback(() => {
    setComparisonList((prev) => [...prev, makeComparison()]);
  }, []);

  const removeComparison = useCallback((id) => {
    setComparisonList((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const updateComparison = useCallback((id, field, value) => {
    setComparisonList((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        if (field === "lifecycle")
          return {
            ...c,
            lifecycle: value,
            referenceType: "",
            reference: "",
            revision: "",
          };
        if (field === "referenceType")
          return { ...c, referenceType: value, reference: "", revision: "" };
        if (field === "reference")
          return { ...c, reference: value, revision: "" };
        return { ...c, [field]: value };
      }),
    );
  }, []);

  // ── Column builder ─────────────────────────────────────────
  const columns = useMemo(() => {
    const buildSubCols = (dataKey, bgColor) =>
      auditMetrics.map((metric) => ({
        accessorKey: `${dataKey}__${metric}`,
        header: (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ fontSize: "0.7rem" }}
            >
              {metric}
            </Typography>
          </Box>
        ),
        size: 110,
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            bgcolor: bgColor,
            borderRight: `1px solid ${theme.palette.divider}`,
            py: 0.5,
          },
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            bgcolor: bgColor,
            borderRight: `1px solid ${theme.palette.divider}`,
            py: 0.5,
          },
        },
        Cell: ({ row }) => {
          const value = row.original?.[`${dataKey}__${metric}`];
          return (
            <Box
              sx={{
                fontWeight: 500,
                color: value === undefined ? "text.disabled" : "text.primary",
                fontSize: "0.85rem",
              }}
            >
              {value ?? "—"}
            </Box>
          );
        },
      }));

    const baseColumns = [
      {
        accessorKey: "semanticId",
        header: "MUID",
        size: 140,
        enablePinning: true,
        muiTableHeadCellProps: { sx: { fontWeight: 700 } },
      },
      {
        accessorKey: "component",
        header: "Component",
        size: 200,
        muiTableHeadCellProps: { sx: { fontWeight: 700 } },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 140,
        muiTableHeadCellProps: { align: "center", sx: { fontWeight: 700 } },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => {
          const statusConfig = {
            Matched: { color: "success", variant: "filled" },
            "Variance Found": { color: "warning", variant: "filled" },
            "Major Variance": { color: "error", variant: "filled" },
            "Critical Variance": { color: "error", variant: "filled" },
            Pending: { color: "default", variant: "outlined" },
          };
          const config = statusConfig[cell.getValue()] ?? {
            color: "default",
            variant: "outlined",
          };
          return (
            <Chip
              size="small"
              label={cell.getValue()}
              color={config.color}
              variant={config.variant}
              sx={{ fontWeight: 600, minWidth: 100, fontSize: "0.75rem" }}
            />
          );
        },
      },
    ];

    const primaryGroupCol = ready
      ? {
          id: "group__primary",
          header: (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "center",
              }}
            >
              <Typography fontWeight={800} sx={{ fontSize: "0.9rem" }}>
                {searchDrawing ? searchDrawing.dwgNumber : selection.reference}
              </Typography>
              <Chip
                label={
                  searchDrawing ? searchDrawing.revision : selection.revision
                }
                size="small"
                sx={{
                  bgcolor: "#7e57c2",
                  color: "white",
                  fontWeight: 700,
                  height: 20,
                  fontSize: "0.7rem",
                  minWidth: 50,
                }}
              />
            </Box>
          ),
          muiTableHeadCellProps: {
            sx: {
              bgcolor: "#ede7f6",
              textAlign: "center",
              borderLeft: "3px solid #7e57c2",
              borderRight: "3px solid #7e57c2",
              borderTop: "2px solid #7e57c2",
              py: 1,
            },
          },
          columns: buildSubCols("primary", "#f3eef9"),
        }
      : null;

    const comparisonGroupCols = completedComparisons.map((entry, i) => {
      const palette = comparisonPalette[i % comparisonPalette.length];
      return {
        id: `group__${entry.id}`,
        header: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "center",
            }}
          >
            <CompareArrowsIcon sx={{ color: palette.border, fontSize: 16 }} />
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "0.85rem",
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {entry.reference}
            </Typography>
            <Chip
              label={entry.revision}
              size="small"
              sx={{
                bgcolor: palette.border,
                color: "white",
                fontWeight: 600,
                height: 20,
                fontSize: "0.7rem",
                minWidth: 45,
              }}
            />
          </Box>
        ),
        muiTableHeadCellProps: {
          sx: {
            bgcolor: palette.bg,
            textAlign: "center",
            borderLeft: `2px solid ${palette.border}`,
            borderRight: `2px solid ${palette.border}`,
            borderTop: `2px solid ${palette.border}`,
            py: 0.75,
          },
        },
        columns: buildSubCols(entry.id, palette.bg),
      };
    });

    return [
      ...baseColumns,
      ...(primaryGroupCol ? [primaryGroupCol] : []),
      ...comparisonGroupCols,
    ];
  }, [
    auditMetrics,
    selection,
    completedComparisons,
    ready,
    theme,
    searchDrawing,
  ]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <Box sx={{ p: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            spacing={1.5}
            direction={{ xs: "column", lg: "row" }}
            alignItems={{ lg: "flex-start" }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "primary.main",
                bgcolor: "primary.50",
                flex: { lg: 1 },
                minWidth: { xs: "100%", lg: "auto" },
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    {/* <Typography
                      fontWeight={700}
                      variant="subtitle2"
                      sx={{ mb: 1 }}
                    >
                      Primary Source
                    </Typography> */}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {[
                        {
                          label: "Lifecycle",
                          key: "lifecycle",
                          options: lifecycleOptions,
                          width: 130,
                        },
                        {
                          label: "Ref Type",
                          key: "referenceType",
                          options: getReferenceTypeOptions(selection.lifecycle),
                          width: 140,
                          disabled: !selection.lifecycle,
                        },
                        {
                          label: "Reference",
                          key: "reference",
                          options: getReferenceOptions(
                            selection.lifecycle,
                            selection.referenceType,
                          ),
                          width: 140,
                          disabled: !selection.referenceType,
                        },
                        {
                          label: "Rev",
                          key: "revision",
                          options: getRevisionOptions(
                            selection.lifecycle,
                            selection.referenceType,
                            selection.reference,
                          ),
                          width: 90,
                          disabled: !selection.reference,
                        },
                      ].map((field) => (
                        <TextField
                          key={field.key}
                          select
                          size="small"
                          label={field.label}
                          value={selection[field.key]}
                          onChange={(e) => {
                            if (field.key === "lifecycle") {
                              setSelection({
                                lifecycle: e.target.value,
                                referenceType: "",
                                reference: "",
                                revision: "",
                              });
                            } else if (field.key === "referenceType") {
                              setSelection({
                                ...selection,
                                referenceType: e.target.value,
                                reference: "",
                                revision: "",
                              });
                            } else if (field.key === "reference") {
                              setSelection({
                                ...selection,
                                reference: e.target.value,
                                revision: "",
                              });
                            } else {
                              setSelection({
                                ...selection,
                                revision: e.target.value,
                              });
                            }
                          }}
                          disabled={field.disabled}
                          sx={{
                            minWidth: field.width,
                            "& .MuiInputBase-root": { fontSize: "0.85rem" },
                            "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                          }}
                          InputLabelProps={{ shrink: true, size: "small" }}
                        >
                          {field.options.map((item) => (
                            <MenuItem
                              key={item}
                              value={item}
                              sx={{ fontSize: "0.85rem" }}
                            >
                              {item}
                            </MenuItem>
                          ))}
                        </TextField>
                      ))}
                    </Stack>
                  </Box>

                  <Box
                    className="search-box"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Autocomplete
                      size="small"
                      options={searchOptions}
                      getOptionLabel={(o) =>
                        `${o.dwgNumber}${o.title ? ` - ${o.title}` : ""}`
                      }
                      inputValue={searchInput}
                      onInputChange={(_, v) => setSearchInput(v)}
                      onChange={(_, v) => {
                        if (v) {
                          setSearchDrawing(v);
                          setSelection({
                            lifecycle: "",
                            referenceType: "",
                            reference: "",
                            revision: "",
                          });
                          setSearchInput("");
                        }
                      }}
                      noOptionsText={
                        searchInput ? "No matches" : "Type to search"
                      }
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.dwgNumber}
                            </Typography>
                            {option.title && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.title}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search"
                          placeholder="dwgNumber or title…"
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": { fontSize: "0.85rem" },
                            "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                          }}
                        />
                      )}
                      sx={{ minWidth: 400 }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {ready && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.default",
                  minWidth: { xs: "100%", sm: 580 },
                }}
              >
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={auditMetricOptions}
                  value={auditMetrics}
                  onChange={(_, value) => setAuditMetrics(value)}
                  sx={{ width: "100%" }}
                  getOptionLabel={(o) => o}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="body2">{option}</Typography>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.length <= 2
                      ? value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{ fontSize: "0.75rem", height: 24 }}
                          />
                        ))
                      : [
                          <Chip
                            key="summary"
                            label={`${value.length} metrics`}
                            size="small"
                            sx={{ fontSize: "0.75rem", height: 24 }}
                          />,
                          <Tooltip key="tooltip" title={value.join(", ")}>
                            <Chip
                              label="…"
                              size="small"
                              sx={{ fontSize: "0.75rem", height: 24 }}
                            />
                          </Tooltip>,
                        ]
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Metrics"
                      placeholder="Select…"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { fontSize: "0.85rem" } }}
                    />
                  )}
                />
              </Paper>
            )}
          </Stack>

          {ready && auditMetrics.length > 0 && (
            <>
              <Divider sx={{ my: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Compare With
                </Typography>
              </Divider>

              <Stack spacing={1}>
                {comparisonList.map((entry, i) => {
                  const palette =
                    comparisonPalette[i % comparisonPalette.length];
                  return (
                    <ComparisonRow
                      key={entry.id}
                      entry={entry}
                      index={i}
                      palette={palette}
                      onUpdate={updateComparison}
                      onRemove={removeComparison}
                      lifecycleOptions={lifecycleOptions}
                      getReferenceTypeOptions={getReferenceTypeOptions}
                      getReferenceOptions={getReferenceOptions}
                      getRevisionOptions={getRevisionOptions}
                    />
                  );
                })}

                <Button
                  startIcon={<AddCircleOutlineIcon fontSize="small" />}
                  variant="text"
                  size="small"
                  onClick={addComparison}
                  sx={{
                    alignSelf: "flex-start",
                    fontSize: "0.8rem",
                    py: 0.5,
                    px: 1,
                    color: "primary.main",
                    "&:hover": { bgcolor: "primary.50" },
                  }}
                >
                  + Add comparison
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      {!canShowTable ? (
        <SingleAuditTablePlaceholder />
      ) : (
        <MaterialReactTable
          columns={columns}
          data={tableData}
          enableColumnActions={false}
          enableSorting={false}
          enableStickyHeader
          enableColumnPinning
          enableDensityToggle={false}
          enableFullScreenToggle
          muiTableContainerProps={{
            sx: {
              maxHeight: "60vh",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            },
          }}
          initialState={{
            density: "compact",
            pagination: { pageSize: 15, pageIndex: 0 },
            columnPinning: { left: ["semanticId", "component"] },
          }}
          muiTableProps={{
            sx: {
              "& .MuiTableCell-root": { fontSize: "0.8rem", py: 0.75 },
              "& .MuiTableHead-root .MuiTableCell-root": {
                fontWeight: 600,
                fontSize: "0.75rem",
              },
            },
          }}
        />
      )}
    </Box>
  );
}

// ─── ComparisonRow — now reads options via helpers instead of static maps ──
function ComparisonRow({
  entry,
  index,
  palette,
  onUpdate,
  onRemove,
  lifecycleOptions,
  getReferenceTypeOptions,
  getReferenceOptions,
  getRevisionOptions,
}) {
  const referenceTypes = getReferenceTypeOptions(entry.lifecycle);
  const references = getReferenceOptions(entry.lifecycle, entry.referenceType);
  const revisions = getRevisionOptions(
    entry.lifecycle,
    entry.referenceType,
    entry.reference,
  );
  const isComplete = entry.reference && entry.revision;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1.5px solid",
        borderColor: palette.border,
        bgcolor: "background.paper",
        position: "relative",
        "&:hover": { boxShadow: 1 },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: palette.bg,
            border: `1px solid ${palette.border}`,
          }}
        >
          <CompareArrowsIcon sx={{ fontSize: 14, color: palette.border }} />
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: palette.border, fontSize: "0.75rem" }}
          >
            #{index + 1}
          </Typography>
        </Box>

        {[
          {
            label: "Lifecycle",
            key: "lifecycle",
            options: lifecycleOptions,
            width: 110,
          },
          {
            label: "Ref Type",
            key: "referenceType",
            options: referenceTypes,
            width: 120,
            disabled: !entry.lifecycle,
          },
          {
            label: "Reference",
            key: "reference",
            options: references,
            width: 120,
            disabled: !entry.referenceType,
          },
          {
            label: "Rev",
            key: "revision",
            options: revisions,
            width: 75,
            disabled: !entry.reference,
          },
        ].map((field) => (
          <TextField
            key={field.key}
            select
            size="small"
            label={field.label}
            value={entry[field.key]}
            onChange={(e) => onUpdate(entry.id, field.key, e.target.value)}
            disabled={field.disabled}
            sx={{
              minWidth: field.width,
              "& .MuiInputBase-root": { fontSize: "0.8rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
            }}
            InputLabelProps={{ shrink: true, size: "small" }}
          >
            {field.options.map((item) => (
              <MenuItem key={item} value={item} sx={{ fontSize: "0.8rem" }}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        ))}

        {isComplete && (
          <Chip
            label={`${entry.reference} • ${entry.revision}`}
            size="small"
            sx={{
              bgcolor: palette.bg,
              color: palette.border,
              fontWeight: 600,
              border: `1px solid ${palette.border}`,
              fontSize: "0.75rem",
              height: 24,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        )}

        <Tooltip title="Remove comparison">
          <IconButton
            size="small"
            onClick={() => onRemove(entry.id)}
            sx={{
              ml: "auto !important",
              color: "text.secondary",
              "&:hover": { color: "error.main", bgcolor: "error.50" },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
